import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

export const styledTableContainerSx = (theme: Theme, borderless?: boolean) => ({
  height: '100%',
  overflow: 'auto',
  ...(borderless ? { border: 'none', boxShadow: 'none' } : {}),
  '&::-webkit-scrollbar': { width: 10, height: 10 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: `${theme.palette.secondary.main}4D`,
    borderRadius: '999px',
    border: '2px solid transparent',
    backgroundClip: 'content-box',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: `${theme.palette.primary.main}66`,
    backgroundClip: 'content-box',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.secondary.main}4D transparent`,
});

/** Wrapper that gives the table area its flex height; scroll is handled by TableContainer */
export const TableScrollBox = styled(Box)({
  minHeight: 320,
  maxHeight: 'auto',
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

/**
 * TableScrollBox variant for tables that should grow with their content instead of
 * scrolling internally — the enclosing page (not this box) owns the scrollbar.
 */
export const NonScrollingTableBox = styled(TableScrollBox)({
  flexShrink: 0,
  overflowY: 'visible',
  minHeight: 0,
});

export const HeaderCell = styled(TableCell, {
  shouldForwardProp: (prop) =>
    prop !== 'sortable' &&
    prop !== 'sortDirection' &&
    prop !== 'bgColor' &&
    prop !== 'fontSize' &&
    prop !== 'paddingX' &&
    prop !== 'paddingY' &&
    prop !== 'textColor' &&
    prop !== 'headerHeight' &&
    prop !== 'headerColor' &&
    prop !== 'headerWeight',
})<{
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | false;
  bgColor?: string;
  fontSize?: string;
  paddingX?: number;
  paddingY?: number;
  textColor?: string;
  headerHeight?: number;
  headerColor?: string;
  headerWeight?: number;
}>(
  ({
    theme,
    sortable,
    bgColor,
    fontSize,
    paddingX,
    paddingY,
    textColor,
    headerHeight,
    headerColor,
    headerWeight,
  }) => ({
    fontWeight: headerWeight ?? 600,
    backgroundColor: bgColor ?? theme.palette.secondary.light,
    color: headerColor ?? textColor ?? theme.palette.secondary.dark,
    padding: `8px ${paddingX ?? 16}px`,
    height: 48,
    verticalAlign: 'middle',
    cursor: sortable ? 'pointer' : 'default',
    userSelect: sortable ? 'none' : 'auto',
    borderBottom: `1px solid ${theme.palette.divider}`,
    // Opt-in compact/uppercase header styling — only applied when a caller passes
    // `headerFontSize`, so existing tables keep their original look.
    ...(fontSize
      ? {
          fontSize,
          height: headerHeight ?? 44,
          padding: `${paddingY ?? 8}px ${paddingX ?? 16}px`,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.06em',
          color: headerColor ?? textColor ?? theme.palette.text.secondary,
          borderBottom: 'none',
        }
      : null),
  }),
);

/** Header checkbox cell — tinted to match the header row */
export const HeaderCheckboxCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<{ bgColor?: string }>(({ theme, bgColor }) => ({
  width: 48,
  padding: '8px 8px 8px 16px',
  backgroundColor: bgColor ?? theme.palette.secondary.light,
}));

/** Body checkbox cell — no background fill */
export const BodyCheckboxCell = styled(TableCell)({
  width: 48,
  padding: '6px 8px 6px 16px',
  height: 52,
});

/** Regular body data cell — consistent row height across all tabs */
export const BodyCell = styled(TableCell, {
  shouldForwardProp: (prop) =>
    prop !== 'rowHeight' &&
    prop !== 'paddingX' &&
    prop !== 'paddingY' &&
    prop !== 'compact' &&
    prop !== 'isFirstRow',
})<{ rowHeight?: number; paddingX?: number; paddingY?: number; compact?: boolean; isFirstRow?: boolean }>(
  ({ theme, rowHeight, paddingX, paddingY, compact, isFirstRow }) => ({
    padding: `${paddingY ?? 10}px ${paddingX ?? 16}px`,
    height: rowHeight ?? 52,
    verticalAlign: 'middle',
    fontSize: 13,
    // Compact tables (opted in via the same flag as HeaderCell's uppercase style) use a
    // borderless-header row-divider convention — first row has no top border, matching
    // the design app's Tailwind `divide-y` treatment — instead of a border under every row.
    ...(compact
      ? {
          borderBottom: 'none',
          borderTop: isFirstRow ? 'none' : '1px solid rgba(34, 28, 90, 0.06)',
        }
      : { borderBottom: `0.5px solid ${theme.palette.divider}` }),
  }),
);

export const LoadingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: `${theme.spacing(6)} 0`,
}));

export const EmptyBox = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(6)} 0`,
  textAlign: 'center' as const,
}));
